import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import debounce from "lodash.debounce";
import { fieldSx } from "../../pages/manage-catalog/constants";

const AutocompleteWithPagination = ({
  selectedItem,
  onDataChange,
  selectedClient,
  width = 250,
  errorMessage,
  hideLabel,
  optionKey,
  apiEndPoint,
  disabled = false,
  customLabel,
  backgroundColor,
  data = [],
  multiple = false,
  supplier,
  inputSx = {},
  containerSx = {},
  compact = false,
  excludeIds = [],
  placeholder = "",
  /** When set, show only N chips + "+X more" (keeps the field single-line). */
  limitTags,
  /** Max height of the options listbox (px). */
  listboxMaxHeight = 280,
  freeSolo = true,
}) => {
  const [allData, setAllData] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [pendingValues, setPendingValues] = useState([]);
  const [multiOpen, setMultiOpen] = useState(false);
  const clientId = localStorage.getItem("client_id");
  const client_ref = useRef();
  const supplier_ref = useRef();
  const searchValueRef = useRef("");
  const pendingValuesRef = useRef([]);
  const onDataChangeRef = useRef(onDataChange);
  const autocompleteRef = useRef(null);
  const useCompactTags = multiple && Number.isInteger(limitTags) && limitTags >= 0;

  useEffect(() => {
    if (data && data.length > 0) {
      const enriched = [...data];
      if (data.length >= 15) {
        enriched.push({ [optionKey]: "Load More", isLoadMore: true });
      }
      setAllData(enriched);
    }
  }, [data, apiEndPoint]);

  let tim;

  const clear_data = () => {
    setAllData([]);
    setInputValue("");
    setPage(0);
    setHasMore(false);
    setLoading(false);
    setLoadMoreLoading(false);
    client_ref.current = null;
    supplier_ref.current = null;
    searchValueRef.current = "";
    autocompleteRef.current = null;
    tim && clearTimeout(tim);
  };

  useEffect(() => {
    client_ref.current = selectedClient?._id || clientId;
  }, [clientId, selectedClient]);

  useEffect(() => {
    if (supplier) {
      supplier_ref.current = supplier;
    } else {
      supplier_ref.current = null;
    }
  }, [supplier]);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  // `optionKey` can be a string (field name) or a function building the label.
  const getLabel = useCallback(
    (option) => {
      if (!option) return "";
      // A bare string is only its own label when there is no key to read: with
      // an `optionKey` set, call sites that hold an unpopulated id string
      // (e.g. `formData.client_id`) must not render the id as the label.
      if (typeof option === "string") return optionKey ? "" : option;
      const label =
        typeof optionKey === "function" ? optionKey(option) : option[optionKey];
      return label == null ? "" : String(label).trim();
    },
    [optionKey],
  );

  const selectedLabel = multiple ? "" : getLabel(selectedItem);
  // Call sites usually build `selectedItem` inline (e.g. `{ code2: value }`), so
  // its identity changes on every render of the parent form. MUI resets the
  // input whenever the `value` identity changes, which wipes whatever is being
  // typed — so key the value we hand to MUI on its content, not its identity.
  const selectedItemKey = useMemo(() => {
    if (multiple || !selectedItem) return "";
    try {
      return JSON.stringify(selectedItem);
    } catch (error) {
      return selectedLabel;
    }
  }, [selectedItem, multiple, selectedLabel]);

  const selectedValue = useMemo(
    () => (selectedLabel && !selectedItem?.isNoData ? selectedItem : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItemKey],
  );

  useEffect(() => {
    if (multiple) {
      const newValues = Array.isArray(selectedItem) ? selectedItem : [];
      setPendingValues(newValues);
      pendingValuesRef.current = newValues;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, multiple]);

  useEffect(() => {
    if (multiple) return;
    setInputValue(selectedLabel);
    searchValueRef.current = selectedLabel;
  }, [selectedLabel, multiple]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        tim = setTimeout(() => {
          setInputValue(searchValueRef.current);
        }, 100);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clear_data();
    };
  }, []);

  const fetchData = useCallback(
    debounce(async (searchQuery, pageNumber, isLoadMore = false) => {
      if (isLoadMore) setLoadMoreLoading(true);
      else setLoading(true);

      try {
        if (typeof apiEndPoint !== "function") {
          console.warn(
            "AutocompleteWithPagination: apiEndPoint is not a function",
            apiEndPoint,
          );
          // If parent passed `data` prop, preserve it instead of overwriting.
          if (data && data.length > 0) {
            // Ensure 'Load More' item is appended when appropriate
            const enriched = [...data];
            if (data.length >= 15) {
              enriched.push({ [optionKey]: "Load More", isLoadMore: true });
            }
            setAllData(enriched);
            setPage(data.length < 15 ? -1 : 0);
            return;
          }

          // No data available from parent, provide fallback
          setAllData([{ [optionKey]: "No data found", isNoData: true }]);
          setPage(-1);
          return;
        }
        const limit = 15;
        const response = await apiEndPoint(
          limit,
          pageNumber * limit,
          searchQuery || undefined,
          client_ref.current,
          supplier_ref.current,
          excludeIds,
        );

        let fetched = [
          ...(response?.data || (Array.isArray(response) ? response : [])),
        ];

        if (pageNumber === 0) setPage(0);

        if (fetched.length === 0) {
          setAllData([{ [optionKey]: "No data found", isNoData: true }]);
          setPage(-1);
          setHasMore(false);
        } else {
          const moreAvailable = fetched.length === limit;
          setHasMore(moreAvailable);
          // Keep a list-row Load More for single-select; multi uses the footer.
          if (moreAvailable) {
            fetched.push({ [optionKey]: "Load More", isLoadMore: true });
          }
          // On new search (pageNumber === 0), completely replace data
          // On load more, append to existing data (drop prior Load More row)
          if (pageNumber === 0) {
            setAllData(fetched);
          } else {
            setAllData((prevData) => [
              ...prevData.filter((item) => !item?.isLoadMore),
              ...fetched,
            ]);
          }
          setPage(moreAvailable ? pageNumber : -1);
        }
      } catch (error) {
        console.error("Fetch failed:", error);
      } finally {
        setLoading(false);
        setLoadMoreLoading(false);
      }
    }, 300),
    [],
  );

  const handleDataChange = (event, value) => {
    if (value?.isLoadMore || value?.isNoData) return;
    if (typeof value === "string") {
      if (!freeSolo) {
        setInputValue(selectedLabel);
        searchValueRef.current = selectedLabel;
        return;
      }
      onDataChange?.(value);
      return;
    }
    // Keep the search text in sync with the picked option, otherwise the
    // click-outside handler below restores the half-typed search term. Free
    // text (freeSolo) is left alone — it is already what the user typed.
    const label = getLabel(value);
    setInputValue(label);
    searchValueRef.current = label;
    onDataChange?.(value);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    searchValueRef.current = value;
    setPage(0);
    setAllData([]);
    fetchData(value, 0);
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason !== "blur") {
      setInputValue(newInputValue);
      searchValueRef.current = newInputValue;
    } else {
      setTimeout(() => setInputValue(searchValueRef.current), 0);
    }
  };

  const handleLoadMoreClick = () => {
    if (page !== -1 && !loading && !loadMoreLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchData(inputValue, nextPage, true);
    }
  };

  const handleTextFieldClick = () => {
    const hasRealData = allData.some(
      (item) => !item.isNoData && !item.isLoadMore,
    );
    if (!loading) {
      fetchData("", 0);
    }
  };

  const handleMultipleChange = (event, values) => {
    if ((values || []).some((v) => v?.isLoadMore)) {
      handleLoadMoreClick();
      return;
    }
    const filtered = (values || []).filter(
      (v) => !v?.isNoData && !v?.isLoadMore,
    );
    setPendingValues(filtered);
    pendingValuesRef.current = filtered;
  };

  const handleSubmitMultiple = useCallback(() => {
    onDataChangeRef.current?.(pendingValuesRef.current);
    setMultiOpen(false);
  }, []);

  const handleSelectAll = () => {
    const allValues = allData.filter((v) => !v?.isNoData && !v?.isLoadMore);
    setPendingValues(allValues);
    pendingValuesRef.current = allValues;
  };

  const optionCount = allData.filter(
    (v) => !v?.isNoData && !v?.isLoadMore,
  ).length;

  const MultiSelectPaper = useMemo(
    () =>
      function MultiSelectPaperComponent({ children, ...props }) {
        return (
          <Paper
            {...props}
            elevation={8}
            sx={{
              ...(props.sx || {}),
              backgroundColor: "var(--option-bg)",
              mt: 0.5,
              overflow: "hidden",
              border: "1px solid var(--option-border)",
              borderRadius: "10px",
            }}
          >
            {children}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderTop: `1px solid var(--option-border)`,
                backgroundColor: "var(--option-bg)",
                display: optionCount > 0 || hasMore ? "flex" : "none",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  disabled={optionCount === 0}
                  size="small"
                  variant="outlined"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSelectAll}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontSize: 12,
                  }}
                >
                  Select All
                </Button>
                {hasMore && (
                  <Button
                    disabled={loading || loadMoreLoading}
                    size="small"
                    variant="text"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleLoadMoreClick}
                    sx={{
                      textTransform: "none",
                      fontFamily: "Poppins",
                      fontSize: 12,
                      minWidth: 0,
                    }}
                  >
                    {loadMoreLoading ? "Loading..." : "Load more"}
                  </Button>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 11,
                    color: "var(--text-secondary)",
                  }}
                >
                  {pendingValues.length} selected
                </Typography>
                <Button
                  disabled={pendingValues.length === 0}
                  size="small"
                  variant="contained"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSubmitMultiple}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontSize: 12,
                  }}
                >
                  Done
                </Button>
              </Box>
            </Box>
          </Paper>
        );
      },
    [
      handleSubmitMultiple,
      optionCount,
      hasMore,
      loading,
      loadMoreLoading,
      pendingValues.length,
      allData,
    ],
  );

  const renderCompactTags = (value, getTagProps) => {
    const visible = value.slice(0, limitTags);
    const hiddenCount = Math.max(0, value.length - visible.length);
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: 0.5,
          maxWidth: "calc(100% - 56px)",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {visible.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const label = getLabel(option);
          return (
            <Chip
              key={key}
              label={label}
              size="small"
              title={label}
              {...tagProps}
              sx={{
                fontFamily: "Poppins",
                fontSize: 11,
                height: 24,
                maxWidth: 140,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  px: 0.75,
                },
              }}
            />
          );
        })}
        {hiddenCount > 0 && (
          <Chip
            size="small"
            label={`+${hiddenCount} more`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setMultiOpen(true)}
            sx={{
              fontFamily: "Poppins",
              fontSize: 11,
              height: 24,
              fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
              bgcolor: "action.selected",
            }}
          />
        )}
      </Box>
    );
  };

  const renderWrappedTags = (value, getTagProps) => (
    <Box
      sx={{
        maxHeight: 96,
        overflowY: "auto",
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        width: "100%",
        py: 0.5,
      }}
    >
      {value.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        const label = getLabel(option);
        return (
          <Chip
            key={key}
            label={label}
            size="small"
            {...tagProps}
            sx={{ fontFamily: "Poppins", fontSize: 11 }}
          />
        );
      })}
    </Box>
  );

  return (
    <div ref={autocompleteRef} style={{ minWidth: 0, ...containerSx }}>
      <Autocomplete
        filterSelectedOptions={!multiple}
        multiple={multiple}
        disableOpenOnFocus={false}
        disableCloseOnSelect={multiple}
        forcePopupIcon
        disabled={disabled}
        size="small"
        id="data-select"
        options={
          multiple
            ? allData.filter((item) => !item?.isLoadMore)
            : allData
        }
        getOptionLabel={(option) => {
          if (!option) return "";
          if (option.isLoadMore) return "Load More";
          if (option.isNoData) return "No data found";
          return getLabel(option);
        }}
        value={multiple ? pendingValues : selectedValue}
        open={multiple ? multiOpen : undefined}
        onOpen={
          multiple
            ? () => {
                setMultiOpen(true);
                if (!loading) fetchData(searchValueRef.current || "", 0);
              }
            : undefined
        }
        onClose={
          multiple
            ? () => {
                setMultiOpen(false);
                const committed = Array.isArray(selectedItem)
                  ? selectedItem
                  : [];
                setPendingValues(committed);
                pendingValuesRef.current = committed;
              }
            : undefined
        }
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onFocusCapture={handleTextFieldClick}
        onChange={multiple ? handleMultipleChange : handleDataChange}
        PaperComponent={multiple ? MultiSelectPaper : undefined}
        ListboxProps={{
          style: {
            maxHeight: listboxMaxHeight,
            overflow: "auto",
            paddingTop: 4,
            paddingBottom: 4,
          },
        }}
        componentsProps={{
          popper: {
            placement: "bottom-start",
            style: { zIndex: 1400 },
            modifiers: [
              {
                name: "offset",
                options: { offset: [0, 4] },
              },
            ],
          },
        }}
        renderTags={
          multiple
            ? useCompactTags
              ? renderCompactTags
              : renderWrappedTags
            : undefined
        }
        filterOptions={(options) => options}
        clearOnEscape
        isOptionEqualToValue={(option, value) => {
          if (option?.isLoadMore || option?.isNoData || value?.isLoadMore)
            return false;
          const optionLabel = getLabel(option);
          const valueLabel = getLabel(value);
          if (option?._id != null && value?._id != null) {
            // Rows can share an _id (e.g. one product with several variant
            // SKUs) while being distinct options — the visible label must
            // also match, otherwise picking one SKU marks all its siblings
            // as selected.
            const sameId = String(option._id) === String(value._id);
            if (optionLabel || valueLabel) {
              return sameId && optionLabel === valueLabel;
            }
            return sameId;
          }
          return optionLabel === valueLabel;
        }}
        freeSolo={freeSolo}
        renderOption={(props, option, { selected }) => {
          const label = getLabel(option);

          if (option.isLoadMore) {
            return (
              <li
                {...props}
                onClick={handleLoadMoreClick}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "var(--option-bg)",
                  borderBottom: `1px solid var(--option-border)`,
                  padding: "10px",
                }}
              >
                <Typography
                  color="primary"
                  variant="body2"
                  style={{ marginRight: 8, color: "var(--text-primary)" }}
                >
                  Load More...
                </Typography>
                {loadMoreLoading && (
                  <CircularProgress color="inherit" size={16} />
                )}
              </li>
            );
          }

          if (option.isNoData) {
            return (
              <li
                {...props}
                aria-disabled="true"
                style={{
                  backgroundColor: "var(--option-bg)",
                  borderBottom: `1px solid var(--option-border)`,
                  padding: "10px",
                  cursor: "not-allowed",
                  pointerEvents: "none",
                }}
              >
                <Typography
                  variant="body2"
                  style={{ color: "var(--text-secondary)" }}
                >
                  No data found
                </Typography>
              </li>
            );
          }

          return (
            <li
              {...props}
              title={label}
              style={{
                backgroundColor: "var(--option-bg)",
                borderBottom: `1px solid var(--option-border)`,
                padding: "10px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {multiple && (
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{ p: 0, mr: 1, flexShrink: 0 }}
                />
              )}
              <p
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  margin: 0,
                  width: "100%",
                  color: "var(--text-primary)",
                }}
              >
                {label}
              </p>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            value={inputValue}
            placeholder={placeholder}
            style={{
              backgroundColor: backgroundColor || "var(--bg-input)",
              borderRadius: compact ? 0 : 10,
            }}
            margin={compact ? "none" : "dense"}
            label={
              hideLabel ? "" : `Select ${customLabel || optionKey || "data"}`
            }
            variant="outlined"
            onChange={handleSearchChange}
            onBlur={() =>
              setTimeout(() => setInputValue(searchValueRef.current), 0)
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              onClick: handleTextFieldClick,
              style: {
                color: "var(--text-primary)",
              },
            }}
            error={!!errorMessage}
            helperText={errorMessage}
            onClick={handleTextFieldClick}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "var(--text-primary)",
                backgroundColor: "var(--bg-input)",
                "& fieldset": {
                  borderColor: "var(--border-default)",
                },
                "&:hover fieldset": {
                  borderColor: "var(--border-hover)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--accent)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "var(--text-secondary)",
                opacity: 1,
              },
              "& .MuiFormLabel-root": {
                color: "var(--text-secondary)",
              },
              ...fieldSx,
              ...inputSx,
            }}
          />
        )}
        style={{ width }}
      />
    </div>
  );
};

export default AutocompleteWithPagination;
